import { TelehealthCta } from "@/components/site/TelehealthCta";
import { storyHeadline, isPhraseLabel } from "@/lib/story-titles";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { faqPageJson } from "@/lib/faq-schema";
import { PHYSICIAN_REF } from "@/lib/medical-schema";

import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { preload } from "react-dom";
import { stories, storySlug, storyCreditSchema, storyImageObject, type ImgRef } from "@/lib/results-stories";
import { storyLinks } from "@/lib/results-story-links";
import ResultsStoryGrid from "@/components/site/ResultsStoryGrid";
import { schemaDateFields } from "@/lib/schema-dates";

/** Container id, so the client can tell server HTML from a fresh mount. */
const GRID_ID = "results-story-grid";

// Client-side navigation only: the story catalog is fetched on demand so a
// direct visit to /results still ships none of it.
const LazyResultsStoryGrid = lazy(
  () => import("@/components/site/ResultsStoryGrid"),
);


export const Route = createFileRoute("/results/")({
  head: () => ({
    meta: [
      { title: "Bariatric Surgery Before & After | Virginia Bariatric" },
      {
        name: "description",
        content:
          "See real before-and-after photos and read inspiring testimonials from Dr. Matthew Fitzer's bariatric surgery patients in Northern Virginia.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Bariatric Surgery Before & After | Virginia Bariatric" },
        { property: "og:description", content: "See real before-and-after photos and read inspiring testimonials from Dr. Matthew Fitzer's bariatric surgery patients in Northern Virginia." },
        { property: "og:url", content: "https://virginiabariatric.com/results" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: "https://virginiabariatric.com/og/results/og-results.png" },
        { property: "og:image:alt", content: "Before and after weight loss results from Dr. Matthew Fitzer's patients at Virginia Bariatric Surgery" },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/og/results/og-results.png" },
        { name: "twitter:image:alt", content: "Before and after weight loss results from Dr. Matthew Fitzer's patients at Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "Bariatric Surgery Before & After | Virginia Bariatric" },
        { name: "twitter:description", content: "See real before-and-after photos and read inspiring testimonials from Dr. Matthew Fitzer's bariatric surgery patients in Northern Virginia." },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/results") : []),
      // No manual LCP preload here: React emits one automatically for the
      // eager, high-priority first story image. A second copy made the
      // browser fetch two variants of slide-29.
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          name: "Patient Results — frequently asked questions",
          mainEntity: RESULTS_FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }),
      },
      // The large gallery/story graph is rendered at the end of <body>
      // instead (see ResultsPage). Everything inside <head> has to be parsed
      // before the browser can paint the first screen, and this graph is tens
      // of kilobytes. Google reads JSON-LD anywhere in the document.
    ] : [],
  }),
  component: ResultsPage,
});



const SITE = "https://virginiabariatric.com";
const RESULTS_URL = `${SITE}/results`;

const RESULTS_FAQS: { question: string; answer: string }[] = [
  {
    question: "How much weight can I expect to lose after bariatric surgery?",
    answer:
      "Most of Dr. Fitzer's gastric sleeve patients lose about 70% of their excess weight, and Virginia Bariatric's own data show patients holding above 70% excess weight loss from year one through year nine. Gastric bypass patients often lose a similar or slightly greater amount. Results vary from patient to patient depending on starting weight, medical conditions, activity and eating habits.",
  },
  {
    question: "How long does it take to see before-and-after results?",
    answer:
      "Weight loss is fastest in the first three to six months after surgery and usually continues for 12 to 18 months. Most of the before-and-after photos on this page were taken between one and two years after surgery.",
  },
  {
    question: "Are these real patients of Dr. Fitzer?",
    answer:
      "Yes. Every before-and-after photo and testimonial on this page comes from an actual Virginia Bariatric Surgery patient who gave permission to share their story. Photos are not stock images or digitally altered.",
  },
  {
    question: "Will I have loose skin after losing the weight?",
    answer:
      "Some patients have loose skin after large amounts of weight loss, and how much depends on age, genetics, starting weight and how long the extra weight was carried. Strength training and steady, well-nourished weight loss help. Body-contouring surgery is an option for patients who are bothered by it once their weight is stable.",
  },
  {
    question: "Can I keep the weight off long term?",
    answer:
      "Yes, with follow-up. Patients who stay in touch with our team, keep protein intake up, stay active and attend support groups maintain their results best. If weight starts to return, we can help with a structured plan and, when appropriate, medical weight-loss medications.",
  },
  {
    question: "How do I get started with weight-loss surgery in Northern Virginia?",
    answer:
      "Request an appointment with Dr. Fitzer at Virginia Bariatric Surgery. We review your medical history and insurance coverage, discuss whether gastric sleeve, gastric bypass, the balloon or medical weight loss fits you best, and walk you through the steps to surgery.",
  },
];


function abs(url: string) {
  return url.startsWith("http") ? url : `${SITE}${url}`;
}

/** Largest generated variant, so crawlers index the highest-quality file. */
function bestUrl(img: ImgRef) {
  if (!img.srcSet) return abs(img.url);
  const last = img.srcSet.split(",").pop()!.trim().split(" ")[0];
  return abs(last);
}

/**
 * Every story is rendered into the initial HTML. Photos below the first story
 * use native loading="lazy", which crawlers index, so all pictures stay
 * associated with this page without adding to the initial download.
 */

function imageNodes() {
  const nodes: Record<string, unknown>[] = [];
  // Every story is present in the initial HTML, so every photo is described.
  stories.forEach((story, index) => {
    const slug = storySlug(story, index);
    if (story.single) {
      nodes.push(
        storyImageObject({
          id: `${RESULTS_URL}#image-${slug}`,
          img: story.before,
          story,
          name: storyHeadline(story.label),
          caption: story.caption,
          isPartOf: `${RESULTS_URL}#gallery`,
        }),
      );
      return;
    }
    nodes.push(
      storyImageObject({
        id: `${RESULTS_URL}#image-${slug}-before`,
        img: story.before,
        story,
        name: `${story.label} — before weight-loss surgery`,
        caption: `${story.label} before weight-loss surgery with Dr. Matthew Fitzer.`,
        isPartOf: `${RESULTS_URL}#gallery`,
      }),
      storyImageObject({
        id: `${RESULTS_URL}#image-${slug}-after`,
        img: story.after,
        story,
        name: `${story.label} — after weight-loss surgery`,
        caption: `${story.label} after weight-loss surgery with Dr. Matthew Fitzer.`,
        isPartOf: `${RESULTS_URL}#gallery`,
      }),
    );
  });
  return nodes;
}


function buildResultsSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalWebPage",
        "@id": `${RESULTS_URL}#webpage`,
        ...workTranslationOf("/results"),
        url: RESULTS_URL,
        name: "Bariatric Surgery Before & After | Virginia Bariatric",
        description:
          "Before-and-after photos and testimonials from Dr. Matthew Fitzer's bariatric surgery patients in Northern Virginia.",
        inLanguage: "en-US",
        specialty: "https://schema.org/Surgical",
        ...schemaDateFields("/results"),
        reviewedBy: PHYSICIAN_REF,
        about: {
          "@type": "MedicalProcedure",
          name: "Bariatric surgery",
        },
        primaryImageOfPage: { "@id": String(imageNodes()[0]!["@id"]) },
        mainEntity: { "@id": `${RESULTS_URL}#storylist` },
        hasPart: [{ "@id": `${RESULTS_URL}#gallery` }, { "@id": `${RESULTS_URL}#storylist` }],
      },
      {
        "@type": "ItemList",
        "@id": `${RESULTS_URL}#storylist`,
        name: "Patient before-and-after stories",
        description:
          "Individual weight-loss surgery patient stories, each with before-and-after photos and details of the procedure.",
        numberOfItems: stories.length,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        // Summary-page form: each entry points at the story page with its
        // name and photo. The full CreativeWork (author, description, about)
        // lives on the story page itself, so repeating it here only inflated
        // the inline payload on /results.
        itemListElement: stories.map((story, index) => {
          const storyUrl = `${SITE}/results/story/${storySlug(story, index)}`;
          return {
            "@type": "ListItem",
            position: index + 1,
            url: storyUrl,
            name: storyHeadline(story.label),
            image: bestUrl(story.single ? story.before : story.after),
          };
        }),
      },
      {
        "@type": "ImageGallery",
        "@id": `${RESULTS_URL}#gallery`,
        name: "Bariatric surgery before-and-after gallery",
        description:
          "Before-and-after photographs of weight-loss surgery patients treated by Dr. Matthew Fitzer at Virginia Bariatric Surgery.",
        isPartOf: { "@id": `${RESULTS_URL}#webpage` },
        image: imageNodes(),
      },
    ],
  };
}



function ResultsPage() {
  // Single high-priority preload for the LCP photo. React dedupes this with
  // the preload it would auto-emit for the eager first image, so the page
  // ships exactly one <link rel="preload"> for slide-29.
  // Server-only: the preload link is already in the delivered HTML, so the
  // browser never needs the story catalog to re-issue it.
  if (import.meta.env.SSR) {
    const lcp = stories[0]!.before;
    preload(lcp.url, {
      as: "image",
      imageSrcSet: lcp.srcSet,
      imageSizes: lcp.sizes,
      fetchPriority: "high",
    });
  }

  // True when this mount has no server-rendered grid to adopt, i.e. the
  // visitor arrived through an in-site link rather than loading /results.
  const [renderGridInBrowser] = useState(
    () =>
      !import.meta.env.SSR &&
      (document.getElementById(GRID_ID)?.childElementCount ?? 0) === 0,
  );

  return (
    <>
      <section className="mx-auto max-w-7xl px-6 pt-6 pb-5 text-center">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif">We are proud of each success story!</h1>
        <p className="mt-2.5 max-w-2xl mx-auto text-muted-foreground">
          Most of the patients in these stories had either gastric sleeve or bypass surgery. To start on your own, call us at{" "}
          <a href="tel:7037099771" aria-label="Call Virginia Bariatric Surgery at 703-709-9771" className="text-brand-logo font-medium">(703) 709-9771</a>{" "}
          or visit our{" "}
          <Link to="/contact" className="text-brand-logo font-medium hover:underline">contact page</Link>.
        </p>

        <TelehealthCta />

        <Link to="/results/share-your-story" className="mt-4 inline-block rounded-full bg-brand-mid px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Share Your Story
        </Link>
      </section>

      <section className="bg-brand-soft/40 py-6">
        {/* The story grid holds no interactive state. When the page is
            delivered by the server, the container carries an empty
            dangerouslySetInnerHTML on the client so React adopts the
            server-rendered DOM as-is: every caption, alt text, photo URL and
            credit stays in the HTML and out of the JavaScript the browser
            downloads and parses. When the page is reached through an in-site
            link there is no server HTML to adopt, so the grid is loaded as a
            separate chunk and rendered normally. */}
        <div
          id={GRID_ID}
          className="mx-auto max-w-[33.408rem] px-6 space-y-4"
          suppressHydrationWarning
          {...(import.meta.env.SSR || renderGridInBrowser
            ? {}
            : { dangerouslySetInnerHTML: { __html: "" } })}
        >
          {import.meta.env.SSR ? (
            <ResultsStoryGrid />
          ) : renderGridInBrowser ? (
            <Suspense fallback={null}>
              <LazyResultsStoryGrid />
            </Suspense>
          ) : null}
        </div>
      </section>

      <nav
        className="mx-auto max-w-4xl px-6 pt-8 pb-2"
        aria-labelledby="all-stories-heading"
      >
        <h2
          id="all-stories-heading"
          className="text-center text-2xl md:text-3xl font-serif text-brand-logo"
        >
          Browse Every Before &amp; After Story
        </h2>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {/* Slug + label only: the one part of the grid that stays hydrated
              for client-side navigation. */}
          {storyLinks.map((s) => (
            <li key={`all-${s.slug}`}>
              <Link
                to="/results/story/$slug"
                params={{ slug: s.slug }}
                className="block rounded-lg border border-border px-3 py-2 text-sm hover:border-brand-logo hover:text-brand-logo"
              >
                {s.name}
                {s.tagline ? (
                  <span className="block text-xs text-muted-foreground">
                    {s.tagline}
                  </span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      </nav>


      <section id="faq" className="mx-auto max-w-3xl px-6 pt-6 pb-2" aria-labelledby="results-faq-heading">
        <h2 id="results-faq-heading" className="text-center text-2xl md:text-3xl font-serif text-brand-logo">
          Before &amp; After Questions Patients Ask
        </h2>
        <dl className="mt-5 space-y-5">
          {RESULTS_FAQS.map((faq, i) => (
            <div key={faq.question} id={`faq-q${i + 1}`} className="rounded-xl border border-border p-5">
              <dt className="font-medium text-lg">{faq.question}</dt>
              <dd className="mt-2 text-foreground/80 leading-[1.6]">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-3xl px-6 pt-4 pb-4 text-center">

        <h2 className="text-2xl md:text-3xl font-serif">We have plenty of room for more stories. Shall we begin yours?</h2>
        <div className="mt-3 flex flex-wrap justify-center gap-3">
          <Link to="/contact" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90">
            Get Started
          </Link>
          <Link to="/" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90">
            Home
          </Link>
        </div>
      </section>

      {/* Gallery + story-list structured data. Rendered here, at the end of
          the document, rather than in <head>: head content blocks first
          paint, and crawlers read JSON-LD from anywhere in the page. */}
      {/* Built on the server only; the browser keeps the delivered script node
          instead of rebuilding ~80 KB of JSON during hydration. */}
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: import.meta.env.SSR ? JSON.stringify(buildResultsSchema()) : "",
        }}
      />
    </>
  );
}
